// The ongoing's email connections (busy-my-web#105) — the lifecycle behind lib/storyConnections.js.
//
// It owns only what the frames say this journey knows: whether the connections filter is open, which
// suggestion is armed, whether the confirmation is showing, and how the removal went. Two things it
// deliberately does NOT own:
//
//   • the connections themselves — they come with the story (`ongoing/currentStory`.emailConnections),
//     so the briefing's count, the middle's listing and this panel cannot disagree about what is
//     connected;
//   • the selection — that is the Work-board's, the same rule the email session follows, which is
//     what draws the frames' "Selected / 14 Emails" panel beside the box.
//
// The wording lives in lib/storyConnections.js; this file is lifecycle only.
import api from '@/lib/services/general.service'
import {
  connectionPick,
  connectionPickId,
  connectionPicks,
  removePath,
  removeTargets,
  removesAllOnCommit,
  removesOnCommit,
  selectedConnections,
} from '@/lib/storyConnections'
import { AUTOMATION_PATH, automationBody, isAutoConnect } from '@/lib/storyAutomation'

export const state = {
  // The "N email connections" row is the filter in force — the frames' closeable chip, with the
  // listing under it in the middle.
  listOpen: false,
  // Which "What would you like to do?" option is armed (the filled radio). Arming is separate from
  // doing, exactly as the frames draw it.
  armed: null,
  // null while choosing, 'confirm' on "Do you want to remove email connection?" (frame 5).
  stage: null,
  // idle | removing | removed | error.
  removeState: 'idle',
  // The band carries a dismiss X.
  noticeDismissed: false,
  // The story's one automation (busy-timeline-srv#21): does mail from these people join by itself,
  // or is it suggested? OFF until the server says otherwise — the safe half of the pair, so a story
  // whose rule has not loaded is never described as auto-connecting.
  autoConnect: false,
  // Which ongoing the loaded rule belongs to, so the switch cannot show story A's answer over
  // story B while B's is still in flight.
  autoConnectFor: null,
  autoConnectSaving: false,
  autoConnectError: false,
}

export const getters = {
  listOpen: s => s.listOpen,
  armed: s => s.armed,
  stage: s => s.stage,
  removeState: s => s.removeState,
  removing: s => s.removeState === 'removing',
  noticeDismissed: s => s.noticeDismissed,
  /**
   * Is a removal receipt waiting to be read? The briefing asks, because removing the LAST
   * connection can empty the strip entirely (an email story with no tasks, notes or packs has no
   * other row), and a receipt that vanished with the row it is reporting would never be seen.
   */
  hasNotice: s => (s.removeState === 'removed' || s.removeState === 'error') && !s.noticeDismissed,

  /** The story's connections, or [] — the one list every surface of this journey reads. */
  connections: (s, g, rootState, rootGetters) =>
    (rootGetters['ongoing/currentStory']?.emailConnections || []).filter(Boolean),

  /**
   * What is ticked, resolved against those connections. Leaving the story, or removing the
   * connection, therefore un-selects it by itself: a pick that no longer names a live connection
   * resolves to nothing.
   */
  selected: (s, g, rootState, rootGetters) =>
    selectedConnections(g.connections, rootGetters['workboard/items'] || []),

  isPicked: (s, g, rootState, rootGetters) => address =>
    (rootGetters['workboard/items'] || []).some(i => i.id === connectionPickId(address)),

  /** The switch's state — only for the story it was loaded for (see `autoConnectFor`). */
  autoConnect: (s, g, rootState, rootGetters) =>
    s.autoConnectFor && s.autoConnectFor === rootGetters['ongoing/currentStory']?.id
      ? s.autoConnect
      : false,
  autoConnectSaving: s => s.autoConnectSaving,
  autoConnectError: s => s.autoConnectError,
}

export const mutations = {
  SET_LIST_OPEN(s, v) { s.listOpen = !!v },
  SET_ARMED(s, v) { s.armed = v || null },
  SET_STAGE(s, v) { s.stage = v || null },
  SET_REMOVE(s, status) {
    s.removeState = status
    s.noticeDismissed = false
  },
  DISMISS_NOTICE(s) { s.noticeDismissed = true },
  SET_AUTO_CONNECT(s, { ongoingId, on }) {
    s.autoConnectFor = ongoingId || null
    s.autoConnect = !!on
  },
  SET_AUTO_SAVING(s, v) { s.autoConnectSaving = !!v },
  SET_AUTO_ERROR(s, v) { s.autoConnectError = !!v },
  RESET(s) {
    s.listOpen = false
    s.armed = null
    s.stage = null
    s.removeState = 'idle'
    s.noticeDismissed = false
    // The rule is NOT reset here: RESET runs whenever the filter is closed, and the story's
    // automation is a fact about the story, not about the panel being open. `reset` (leaving the
    // story) clears it.
  },
  CLEAR_AUTO(s) {
    s.autoConnect = false
    s.autoConnectFor = null
    s.autoConnectSaving = false
    s.autoConnectError = false
  },
}

export const actions = {
  /**
   * The Briefing's "N email connections" row: open the listing, or close it again (its X).
   *
   * Closing puts the whole journey back — a half-armed panel left behind a closed filter would come
   * back armed the next time the row is clicked. The SELECTION is dropped with it, because it is
   * what the filter was about (the frames' "Clear selections" does the same thing on its own).
   */
  toggleList({ commit, state: s, dispatch, rootGetters }) {
    const next = !s.listOpen
    commit('RESET')
    commit('SET_LIST_OPEN', next)
    if (!next) dispatch('clearSelection')
    // Opening is when the switch is about to be drawn, so it is when its state is read
    // (busy-timeline-srv#21) — one call, and only for a story that has connections to switch on.
    if (next) dispatch('loadAutomation', rootGetters['ongoing/currentStory']?.id)
  },

  /** Ticking a connection in the listing — straight onto the Work-board, which the box reads. */
  toggle({ dispatch, commit }, connection) {
    if (!connection?.emailAddress) return
    dispatch('workboard/toggle', connectionPick(connection), { root: true })
    // A changed selection is a changed question: whatever was armed was armed about the old one.
    commit('SET_ARMED', null)
    commit('SET_STAGE', null)
  },

  /** The listing's header tick — all of them, or none (the frame's select-all box). */
  toggleAll({ dispatch, getters: g, commit }) {
    const all = g.connections
    const allPicked = all.length > 0 && all.every(c => g.isPicked(c.emailAddress))
    for (const c of all) {
      const picked = g.isPicked(c.emailAddress)
      if (allPicked && picked) dispatch('workboard/remove', connectionPickId(c.emailAddress), { root: true })
      if (!allPicked && !picked) dispatch('workboard/toggle', connectionPick(c), { root: true })
    }
    commit('SET_ARMED', null)
    commit('SET_STAGE', null)
  },

  /** "Clear selections" — this journey's picks only; anything else on the board stays. */
  clearSelection({ dispatch, commit, rootGetters }) {
    for (const p of connectionPicks(rootGetters['workboard/items'] || [])) {
      dispatch('workboard/remove', p.id, { root: true })
    }
    commit('SET_ARMED', null)
    commit('SET_STAGE', null)
  },

  /** Choosing a suggestion arms it (the filled radio); choosing it again disarms. */
  arm({ commit, dispatch, getters: g, state: s }, id) {
    const on = s.armed !== id
    commit('SET_ARMED', on ? id : null)
    commit('SET_STAGE', null)
    // "Remove email connections" is the at-rest offer (2026-09-15 parity audit F2-6,
    // busy-my-web#500): it is about ALL of them, so arming it ticks them — the confirmation, the
    // commit and the receipt then read the one selection every other path in this journey reads,
    // rather than a second notion of "all".
    if (on && removesAllOnCommit(id)) {
      for (const c of g.connections || []) {
        if (!(g.selected || []).some(x => x.emailAddress === c.emailAddress)) dispatch('toggle', c)
      }
    }
  },

  /**
   * The primary button. It is ONE button that changes its word between the two steps, as the frames
   * do: "Next" opens the confirmation, "Remove" carries it out. Reply and Forward are the story's
   * own actions and go straight through — there is nothing to confirm about writing an email.
   */
  async commitArmed({ commit, dispatch, state: s }) {
    if (s.armed === 'reply' || s.armed === 'forward') {
      window.__busySuggest?.(s.armed === 'reply' ? 'replyEmail' : 'forwardEmail')
      commit('SET_ARMED', null)
      return null
    }
    if (!removesOnCommit(s.armed)) return null
    if (s.stage !== 'confirm') { commit('SET_STAGE', 'confirm'); return null }
    return dispatch('removeSelected')
  },

  /** The confirmation's back arrow: out of the question, still armed on the option that asked it. */
  backFromConfirm({ commit }) { commit('SET_STAGE', null) },

  /** Its close: out of the question AND off the option — the selection itself stays. */
  closeConfirm({ commit }) {
    commit('SET_STAGE', null)
    commit('SET_ARMED', null)
  },

  dismissNotice({ commit }) { commit('DISMISS_NOTICE') },

  /**
   * Remove the ticked connections (frame 6).
   *
   * One DELETE per connection, then the story is re-read: the count on the briefing row, the
   * listing, and the permanent "Email connection removed" step the service writes all come from
   * that one answer, so none of them is patched here into saying something the server has not
   * said. The emails the connection brought in STAY on the story — unfollowing stops future mail,
   * which is why the "N emails" row does not move.
   *
   * Removing the last connection leaves the ongoing exactly where it is: the row simply goes (the
   * briefing draws it only when there are connections), so the filter closes with it.
   */
  async removeSelected({ commit, dispatch, getters: g, rootGetters, state: s }) {
    const targets = removeTargets(g.selected)
    const storyId = rootGetters['ongoing/currentStory']?.id
    if (!targets.length || !storyId || s.removeState === 'removing') return false
    commit('SET_REMOVE', 'removing')
    try {
      for (const address of targets) {
        await api.deleteWithPath('ongoing', storyId, removePath(address))
      }
      // Off the board before the re-read: they are no longer something to have selected.
      for (const address of targets) {
        dispatch('workboard/remove', connectionPickId(address), { root: true })
      }
      commit('SET_ARMED', null)
      commit('SET_STAGE', null)
      const story = await dispatch('ongoing/getStory', storyId, { root: true })
      // The sidebar's list orders on the ongoing's update time, which the removal moved.
      dispatch('ongoing/fetch', null, { root: true })
      commit('SET_REMOVE', 'removed')
      // Nothing left to list — the row is gone from the briefing, so the filter goes with it.
      if (!(story?.emailConnections || []).length) commit('SET_LIST_OPEN', false)
      return true
    } catch {
      commit('SET_REMOVE', 'error')
      return false
    }
  },

  /**
   * Read this story's automation rule (busy-timeline-srv#21). Best-effort: a story whose rule
   * cannot be read is shown as OFF, which is both the default and the safe half — the switch would
   * otherwise claim an automation the server may not have.
   */
  async loadAutomation({ commit }, ongoingId) {
    if (!ongoingId) return false
    commit('SET_AUTO_ERROR', false)
    try {
      const res = await api.getWithPathById('ongoing', ongoingId, AUTOMATION_PATH)
      commit('SET_AUTO_CONNECT', { ongoingId, on: isAutoConnect(res?.data?.data ?? res?.data) })
      return true
    } catch {
      commit('SET_AUTO_CONNECT', { ongoingId, on: false })
      return false
    }
  },

  /**
   * Flip the switch. Optimistic — the toggle answers the click at once — and put back on a failure
   * with the panel's own error line, because a switch that silently stayed where it was would be
   * read as "the automation is on".
   */
  async setAutoConnect({ commit, state: s, rootGetters }, on) {
    const ongoingId = rootGetters['ongoing/currentStory']?.id
    if (!ongoingId || s.autoConnectSaving) return false
    const previous = s.autoConnectFor === ongoingId ? s.autoConnect : false
    commit('SET_AUTO_CONNECT', { ongoingId, on })
    commit('SET_AUTO_SAVING', true)
    commit('SET_AUTO_ERROR', false)
    try {
      const res = await api.updateWithPath('ongoing', ongoingId, AUTOMATION_PATH, automationBody(on))
      const answered = res?.data?.data ?? res?.data
      // The server's own answer wins over the optimistic one where it says anything.
      commit('SET_AUTO_CONNECT', { ongoingId, on: answered ? isAutoConnect(answered) : !!on })
      return true
    } catch {
      commit('SET_AUTO_CONNECT', { ongoingId, on: previous })
      commit('SET_AUTO_ERROR', true)
      return false
    } finally {
      commit('SET_AUTO_SAVING', false)
    }
  },

  /** Leaving the story: nothing about one ongoing's connections belongs to the next one. */
  reset({ commit }) {
    commit('RESET')
    commit('CLEAR_AUTO')
  },
}
